import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, LP_POSTER, PLAYABLE_PREVIEW, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';

/** The card's poster: a landscape frame (a desktop page's first screen), the page drawn over a quiet placeholder; or,
 *  `$fill`, the whole of its parent's frame (a draft card's portrait poster, a phone's first screen). */
export const PosterBox = styled.div<{ $fill?: boolean }>`
  position: relative;
  width: 100%;
  ${(p) => (p.$fill ? 'height: 100%;' : 'aspect-ratio: 16 / 10;')}
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.surfaceInset};
`;

/** Under the page: what shows until it has loaded, and in its place when the page has nothing to draw. */
export const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[8]};
  color: ${COLORS.textFaint};
`;

export const PlaceholderText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/** The page at the top of its first screen, over the placeholder: hidden until its document has loaded, then faded
 *  in. Never interactive (the card's own links open the live page). */
export const FrameLayer = styled.div<{ $shown: boolean }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  background: ${COLORS.surfaceInset};
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${PLAYABLE_PREVIEW.SWAP_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }

  /* The preview box rounds its corners for a framed preview; the card's poster is full bleed. */
  & > * {
    border-radius: 0;
  }
`;

/** Moves a cropped page up by `$shift` px so the kept part of it sits in the frame, and left by `$inset` px so its
 *  overscan is cropped evenly at both sides. */
export const FrameShift = styled.div<{ $shift: number; $inset: number }>`
  transform: translate(${(p) => -p.$inset}px, ${(p) => -p.$shift}px);
`;

/** A draft's key art under its page (shown until the page has loaded, and in its place when it cannot be drawn).
 *  `$waiting`: the page is on its way, so the art is only a strongly blurred colour field under the loader. */
export const PlaceholderImg = styled.img<{ $waiting?: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: ${(p) => (p.$waiting ? `blur(${LP_POSTER.WAIT_BLUR_PX}px) brightness(0.82)` : 'none')};
  transform: ${(p) => (p.$waiting ? `scale(${LP_POSTER.WAIT_SCALE})` : 'none')};
  transition:
    filter ${PLAYABLE_PREVIEW.SWAP_MS}ms ${ANIM.EASE},
    transform ${PLAYABLE_PREVIEW.SWAP_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** The animated Whitelabels loader over the blurred art while the page renders (the app's own loader, as in the
 *  configurator's previews); it fades out as the page fades in. */
export const Loader = styled.img<{ $shown: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1;
  width: ${LP_POSTER.LOADER_PX}px;
  height: ${LP_POSTER.LOADER_PX}px;
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${PLAYABLE_PREVIEW.SWAP_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;
